// material
import { experimentalStyled as styled } from '@mui/material/styles';
import { Box, Button, Container, Typography } from '@mui/material';
// layouts
import LogoOnlyLayout from 'src/components/layouts/LogoOnlyLayout';
// route
import { PATH_AUTH } from 'src/routes/paths';
// components
import Page from 'src/components/Page';
import { MfaMethodSelectForm } from './components/form';
// hooks
import { useEffect, useState } from 'react';
import useAuth from 'src/hooks/useAuth';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { AVAILABLE_MFA_METHODS } from 'src/@types/authentication';

// ----------------------------------------------------------------------

const RootStyle = styled(Page)(({ theme }) => ({
  display: 'flex',
  minHeight: '100%',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(12, 0),
}));

// MFA設定画面遷移
export type MfaSetNavigation = {
  next: string; // 次の画面Path
  back: string; // 前の画面Path
  caller?: string; // 呼び出し元画面Path
};

// MFA設定画面遷移デフォルト設定
export const mfaSetFlowPaths: MfaSetNavigation = {
  next: PATH_AUTH.addTotpDevice, // TOTPデバイス追加画面
  back: PATH_AUTH.selectMfaMethod, // MFA選択画面
};

// ----------------------------------------------------------------------
/**
 * MFA方式選択画面
 * @return コンポーネント
 */
export const SelectMfaMethod = () => {
  // Hooks
  const { logout, getRegisteredMfaMethod, isRequiredMfa, isMfaAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { state } = useLocation();
  const { t } = useTranslation();

  // state
  const [callerPath, setCallerPath] = useState<string | null>(null); // 呼び出し元画面Path
  const [navigationPaths, setNavigationPaths] = useState<MfaSetNavigation>(mfaSetFlowPaths);
  const [registeredMfaMethod, setRegisteredMfaMethod] = useState<AVAILABLE_MFA_METHODS | null>(null);

  useEffect(() => {
    // 画面遷移先設定
    if (state?.caller) {
      setCallerPath(state.caller);
      setNavigationPaths({ ...mfaSetFlowPaths, caller: state.caller });
    }
  }, [state?.caller]);

  // 初期処理
  useEffect(() => {
    // ユーザーの登録済みMFA方法を取得
    const registeredMfaMethod = async () => {
      const method = await getRegisteredMfaMethod();
      setRegisteredMfaMethod(method);
    };
    // MFA方式設定済みのユーザーの場合：
    if (isMfaAuthenticated) {
      // 認証済みのMFA方法を初期表示時に選択する
      registeredMfaMethod();
    }
  }, []);

  // 戻るボタン押下
  const handleBack = async () => {
    // MFA未認証のユーザーの場合：ログアウトしてログイン画面へ
    if (isRequiredMfa && !isMfaAuthenticated) {
      await logout();
    }
    // 呼び出し元画面の設定があれば (=ログイン後の画面から遷移した場合)、その画面へ戻る
    return navigate(callerPath ?? '/', { replace: true });
  };

  return (
    <RootStyle title="Select MFA Method | Minimal UI">
      <LogoOnlyLayout />
      <Container>
        <Box sx={{ maxWidth: 480, mx: 'auto' }}>
          <Typography variant="h3" paragraph>
            {t('mfa.title.selectMfaMethod')}
          </Typography>
          <MfaMethodSelectForm navigationPaths={navigationPaths} registeredMfaMethod={registeredMfaMethod} />
          <Button fullWidth size="large" sx={{ mt: 1 }} onClick={handleBack}>
            {isRequiredMfa && !isMfaAuthenticated ? t('button.backToLogin') : t('button.back')}
          </Button>
        </Box>
      </Container>
    </RootStyle>
  );
};
